import {
    Button,
    Card,
    CardContent,
    CardHeader,
    colors,
    Grid,
    Switch,
    TextField,
    Theme,
    Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect, useState} from 'react';
import {RoomParty, RoomPartyMember} from '../../../graphql/groups.datatypes';
import {Can} from '../../../components/Can';
import ConfirmDelete from '../../../components/ConfirmDelete';
import {getHandleFieldChange, getHasError} from '../../../utils/formHelpers';
import validate from 'validate.js';
import createRoomPartyMember, {CreateRoomPartyMemberInput} from './createRoomPartyMember.api';
import updateRoomPartyMember, {UpdateRoomPartyMemberInput} from './updateRoomPartyMember.api';
import deleteRoomPartyMember from './deleteRoomPartyMember.api';
import {useSnackbar} from 'notistack';
import {formatDate, formatGraphQlDate} from '../../../utils/formatters';
import moment from 'moment';
import {getGroupNightsData} from '../../BookingTool/helperFunctions';
import {checkRoomAvailable} from '../../Resources/AvailableHotelModal/AvailableHotelModal';
import anyLoading from '../../../utils/anyLoading';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import changeRoomLeader from './changeRoomLeader.api';
import getBillingPartyPayingMembers from './getBillingPartyPayingMembers.api';
import {OPERATOR} from '../../../graphql/common.datatypes';
import clsx from 'clsx';
import {WCVDatePicker} from '../../../components/DateUtilsProvider/WCVDatePicker';

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    saveButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: colors.green[600],
        '&:hover': {
            backgroundColor: colors.green[900]
        }
    },
    deleteButton: {
        marginLeft: theme.spacing(1),
        color: theme.palette.white,
        backgroundColor: theme.palette.error.light,
        '&:hover': {
            backgroundColor: theme.palette.error.main
        }
    },
}));

const RoomPartyMemberEdit = (props: {
    roomParty: RoomParty,
    roomPartyMember: RoomPartyMember,
    setSelectedRoomGuest: (member: RoomPartyMember | null) => void,
    setSelectedModal: (modelSelection: string) => void;
    className?: string,
}) => {
    const {roomParty, roomPartyMember, className, setSelectedRoomGuest, setSelectedModal} = props;
    const classes = useStyles();
    const isNewMember = roomPartyMember.id === -1;

    const where = {AND: [{column: 'disabled', operator: OPERATOR.EQ, value: false}]};
    const orderBy = [{column: 'created_at', order: 'DESC'}];
    const hasGroupBooking = {column: 'GROUP_ID', operator: OPERATOR.EQ, value: props.roomParty!.booking!.group_id!};
    const billingPartiesData = getBillingPartyPayingMembers(where, orderBy, hasGroupBooking);

    const [sendCreateRoomPartyMember, createData] = createRoomPartyMember();
    const [sendUpdateRoomPartyMember, updateData] = updateRoomPartyMember();
    const [sendDeleteRoomPartyMember, deleteData] = deleteRoomPartyMember();
    const {enqueueSnackbar} = useSnackbar();
    const [open, setOpen] = React.useState(false);
    const [formState, setFormState] = useState({...roomPartyMember});
    const [groupNights, setGroupNights] = useState(getGroupNightsData(roomParty.booking!.group!));
    const totalNightsAllowed = groupNights.mandatoryNightsCount + groupNights.maxAdditionalNights;

    const [sendChangeRoomLeader, changeRoomLeaderData] = changeRoomLeader();
    const handleChangeRoomLeader = (memberId: number) => {
        sendChangeRoomLeader({variables: {data: {room_party_member_id: memberId}}})
            .then(() => {
                enqueueSnackbar('Room Leader Updated.', {variant: 'success'});
                setSelectedRoomGuest(null);
                setSelectedModal('')
            })
            .catch(() => {
                enqueueSnackbar('Change Room Leader Failed!', {variant: 'error'});
                setSelectedRoomGuest(null);
                setSelectedModal('')
            });
    }



    const loadingData = anyLoading(createData, updateData, deleteData, changeRoomLeaderData, billingPartiesData);

    useEffect(() => {
        setFormState({...roomPartyMember});
        setGroupNights(getGroupNightsData(roomParty.booking!.group!));
    }, [roomParty]);

    const [errors, setErrors] = useState({
        isValid: false,
        roomAvailable: false,
        rangeInvalid: false,
        errors: {
            name: false,
            description: false,
            start_date: false,
            end_date: false,
            admissions_cutoff: false,
        }
    });
    //2. Setup helper functions for form
    const handleFieldChange = getHandleFieldChange(setFormState);
    const hasError = getHasError(errors);

    //3. setup validation and error hook
    const validateSchema: any = {
        group_member_id: {
            numericality: {
                onlyInteger: true,
                greaterThan: 0,
                message: 'is required'
            }
        },
        billing_party_id: {
            numericality: {
                onlyInteger: true,
                greaterThan: 0,
                message: 'is required'
            }
        },
    };

    useEffect(() => {
        const newErrors = validate(formState, validateSchema);
        const start = moment(new Date(formState.start_date!));
        const end = moment(new Date(formState.end_date!));
        const bookingLength = end.diff(start, 'days');

        const roomAvailable = !roomParty.members_different_dates || checkRoomAvailable(
            roomParty.hotel!,
            start,
            end,
            roomParty.members!.length
        );

        const rangeInvalid = roomParty.members_different_dates! && (start.isAfter(groupNights.mandatoryStart, 'days') ||
            end.isBefore(groupNights.mandatoryEnd, 'days') ||
            bookingLength > totalNightsAllowed);

        setErrors(state => ({
            ...state,
            roomAvailable,
            rangeInvalid,
            isValid: !newErrors && roomAvailable && !rangeInvalid,
            errors: newErrors || {}
        }));
    }, [formState]);

    //4. Handle save form
    const onSaveClick = () => {
        if (isNewMember) {
            const sanitisedData: CreateRoomPartyMemberInput = {
                data: {
                    room_party_id: formState.room_party_id!,
                    group_member_id: formState.group_member_id!,
                    primary_occupant: formState.primary_occupant!,
                    billing_party_id: formState.billing_party_id!,
                    guest_number: formState.guest_number!,
                    start_date: formState.start_date ? formatGraphQlDate(formState.start_date)! : '',
                    end_date: formState.end_date ? formatGraphQlDate(formState.end_date)! : '',
                }
            };

            sendCreateRoomPartyMember({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Room Party Member Saved.', {variant: 'success'});
                    setSelectedRoomGuest(null);
                    setSelectedModal('')
                })
                .catch(() => {
                    enqueueSnackbar('Save Room Party Member Failed!', {variant: 'error'});
                });
        } else {
            const sanitisedData: UpdateRoomPartyMemberInput = {
                data: {
                    id: formState.id,
                    //room_party_id: formState.room_party_id!,
                    //group_member_id: formState.group_member_id!,
                    //primary_occupant: formState.primary_occupant!,
                    billing_party_id: formState.billing_party_id!,
                    //guest_number: formState.guest_number!,
                    start_date: formState.start_date ? formatGraphQlDate(formState.start_date) : '',
                    end_date: formState.end_date ? formatGraphQlDate(formState.end_date) : '',
                }
            };

            sendUpdateRoomPartyMember({variables: sanitisedData})
                .then(() => {
                    enqueueSnackbar('Room Party Member Updated.', {variant: 'success'});
                    setSelectedRoomGuest(null);
                    setSelectedModal('')
                })
                .catch(() => {
                    enqueueSnackbar('Room Party Member Update Failed!', {variant: 'error'});
                });
        }

    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleDelete = () => {
        sendDeleteRoomPartyMember({variables: {id: formState.id}})
            .then(() => {
                enqueueSnackbar('Room Party Member Deleted.', {variant: 'success'});
                setSelectedRoomGuest(null);
                setSelectedModal('');
            })
            .catch(() => {
                enqueueSnackbar('Deleted Room Party member Failed!', {variant: 'error'});
            });

        handleClose();
    };

    return (
        <Card className={clsx([className, classes.container])}>
            <LoadingOverlay loading={loadingData.loading}>
                <CardHeader title="Room Party Member Details"/>
                <CardContent className={classes.card}>
                    <Grid container spacing={3}>

                        {!isNewMember && (
                            <Grid item xs={12}>
                                <Grid component="label" container alignItems="center" spacing={1}>
                                    <Grid item>
                                        <Switch
                                            checked={props.roomPartyMember.primary_occupant}
                                            color="secondary"
                                            edge="start"
                                            name="primary_occupant"
                                            onChange={() => !props.roomPartyMember.primary_occupant ? handleChangeRoomLeader(props.roomPartyMember.id!) : null}
                                            value={props.roomPartyMember.primary_occupant}
                                            disabled={props.roomPartyMember.primary_occupant}
                                        />
                                    </Grid>
                                    <Grid item><Typography variant="h5">Primary Occupant?</Typography></Grid>
                                </Grid>
                            </Grid>
                        )}

                        {isNewMember && (
                            <Grid item xs={12}>
                                <Typography variant="body1">
                                    Please make sure that you have created the group member that you want to add to this
                                    room as an occupant.
                                </Typography>
                            </Grid>
                        )}

                        {isNewMember && (
                            <Grid item xs={12}>
                                <TextField
                                    {...hasError('group_member_id')}
                                    fullWidth
                                    label="Available Group Members"
                                    name="group_member_id"
                                    onChange={handleFieldChange}
                                    select
                                    // eslint-disable-next-line react/jsx-sort-props
                                    SelectProps={{native: true}}
                                    value={formState.group_member_id}
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                >
                                    <option value={-1}>please select</option>
                                    {roomParty.booking &&
                                    roomParty.booking.group &&
                                    roomParty.booking.group.members &&
                                    roomParty.booking.group.members
                                        .filter(groupMember => !groupMember.roomMember || groupMember.roomMember.id === roomPartyMember.id)
                                        .map((groupMember) => (
                                            <option key={groupMember.id} value={groupMember.id}>
                                                {groupMember.first_name} {groupMember.last_name}
                                            </option>
                                        ))}
                                </TextField>
                            </Grid>
                        )}

                        {!isNewMember && formState.groupMember && (
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Group Member"
                                    name="group_member_id"
                                    value={formState.groupMember.first_name + ' ' + formState.groupMember.last_name}
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    disabled={true}
                                />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <TextField
                                {...hasError('billing_party_id')}
                                fullWidth
                                label="Billing Party"
                                name="billing_party_id"
                                onChange={handleFieldChange}
                                select
                                // eslint-disable-next-line react/jsx-sort-props
                                SelectProps={{native: true}}
                                value={formState.billing_party_id}
                                variant="outlined"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            >
                                {formState.billing_party_id === -1 && <option value={-1}>please select</option>}
                                {billingPartiesData.data &&
                                billingPartiesData.data.allBillingParties &&
                                billingPartiesData.data.allBillingParties.map((billingParty) => {
                                    const primary = billingParty.payingMembers!.find(member => member.primary_paying_member);
                                    return (
                                        <option key={billingParty.id} value={billingParty.id}>
                                            {primary && (
                                                  `${primary.first_name} ${primary.last_name} (${primary.email})`
                                            )}
                                        </option>
                                    )
                                })}
                            </TextField>
                        </Grid>

                        {roomParty.members_different_dates && (
                            <React.Fragment>

                                {!errors.roomAvailable && (
                                    <Grid item xs={12}>
                                        <Typography variant={'body1'} color={'error'}>
                                            Room Unavailable in hotel for these dates.
                                        </Typography>
                                    </Grid>
                                )}
                                {errors.rangeInvalid && (
                                    <Grid item xs={12}>
                                        <Typography variant={'body1'} color={'error'}>
                                            Date range is invalid. Group stay constraints: <br/>
                                            Mandatory start: {formatDate(groupNights.mandatoryStart)} <br/>
                                            Mandatory end: {formatDate(groupNights.mandatoryEnd)}<br/>
                                            Max Stay: {totalNightsAllowed} nights. <br/>
                                        </Typography>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                        <Grid container spacing={3}>
                                            <Grid item md={4} xs={6}>
                                                <WCVDatePicker
                                                    {...hasError('start_date')}
                                                    id="start_date"
                                                    label="Check In:"
                                                    value={formState.start_date}
                                                    onChange={(date, value) => {
                                                        setFormState({
                                                            ...formState,
                                                            start_date: value ? value : undefined
                                                        })
                                                    }}
                                                    disabled={isNewMember}
                                                />
                                            </Grid>
                                            <Grid item md={4} xs={6}>
                                                <WCVDatePicker
                                                    {...hasError('end_date')}
                                                    id="end_date"
                                                    label="Check Out:"
                                                    value={formState.end_date}
                                                    onChange={(date, value) => {
                                                        setFormState({
                                                            ...formState,
                                                            end_date: value ? value : undefined
                                                        })
                                                    }}
                                                    disabled={isNewMember}
                                                />
                                            </Grid>
                                        </Grid>
                                </Grid>
                            </React.Fragment>
                        )}

                        <Grid item xs={12}>
                            <Button className={classes.saveButton} onClick={onSaveClick} variant="contained"
                                    disabled={!errors.isValid}>Save</Button>

                            <Can permission="room_party.update">
                                <Button onClick={handleClickOpen} className={classes.deleteButton}
                                        variant="contained"
                                        disabled={isNewMember}>
                                    Remove From Room
                                </Button>
                            </Can>

                            <Button
                                variant={'outlined'}
                                onClick={
                                () => {
                                    setSelectedRoomGuest(null)
                                    setSelectedModal('')
                                }
                            }>Cancel
                            </Button>

                        </Grid>

                    </Grid>
                </CardContent>
                <ConfirmDelete onClose={handleClose} onDeleteClick={handleDelete} open={open}/>
            </LoadingOverlay>
        </Card>
    );
};

export default RoomPartyMemberEdit;
